import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios, {
  decryptData,
  removeBracketedText,
  formatType,
} from "../Axiosinstance";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { styled } from "@mui/system";
import { logoImage } from "../common"; // Ensure this path is correct

const CustomPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: "15px",
  boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
  backgroundColor: "#ffffff",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const PaymentsPage = () => {
  const location = useLocation();
  const navigate = useNavigate(); // Added for navigation
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [amount, setAmount] = useState("");
  const [errorMessage, setErrorMessage] = useState(""); // Added for amount error
  const [openDialog, setOpenDialog] = useState(false);
  const [paymentSessionUrl, setPaymentSessionUrl] = useState("");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const queryParams = new URLSearchParams(location.search);
  const encryptedData = queryParams.get(""); // Update query parameter key if necessary

  useEffect(() => {
    const fetchData = async () => {
      let id = null;
      if (encryptedData) {
        try {
          const decryptedData = decryptData(decodeURIComponent(encryptedData));
          id = decryptedData.id;

          if (id) {
            try {
              const [registrationResponse, paymentResponse] = await Promise.all(
                [
                  axios.get(`/registrationadvances/${id}`),
                  axios.get(`/registrationadvances/paymentDetails-info/${id}`),
                  
                ]
              );

              setData(registrationResponse.data);
              setPaymentInfo(paymentResponse.data);
            } catch (error) {
              console.error("Error fetching data:", error);
              setError("Failed to fetch payment data");
            }
          }
        } catch (error) {
          console.error("Failed to decrypt data:", error);
          setError("Failed to decrypt data");
        }
      } else {
        setError("No encrypted data found");
      }
      setLoading(false);
    };

    fetchData();
  }, [encryptedData]);

  const handleProceed = async () => {
    setErrorMessage("");
    if (amount) {
      const numericAmount = parseFloat(amount);
      if (numericAmount < 1 || numericAmount > paymentInfo.totalWanttoPaid) {
        setErrorMessage(
          `The amount must be between 1 and ${paymentInfo.totalWanttoPaid} SGD.`
        );
        return;
      }

      try {
        const id = data?.id;
        // Call PaymentAmount API
        await axios.post(`registrationadvances/${id}/PaymentAmount`, {
          paymentAmount: numericAmount,
        });
        // Proceed to open dialog
        setOpenDialog(true);
      } catch (error) {
        console.error("Error processing payment amount:", error);
        setErrorMessage("Failed to process payment amount. Please try again.");
      }
    } else {
      setErrorMessage("Please enter a valid amount.");
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handlePaymentChoice = () => {
    const id = data?.id;

    const successUrl = `${window.location.origin}/Invoice/jurong_academy_${data?.id}_invoice`;
    const cancelUrl = window.location.href;
    //    const newPaymentType = paymentMethod === 'online' ? 'online' : 'offline'; // Determine payment type based on selected method

    // Ensure payment amount is within the valid range for online payments
    if (amount < 50 || amount > 5000) {
      setError("The amount must be between 50 SGD and 5000 SGD.");
      return;
    }

    axios
      .post("/api/v1/stripe/create-payment", {
        amount: amount * 100, // Convert to cents
        quantity: 1,
        currency: "sgd",
        name: data?.name,
        successUrl: successUrl,
        cancelUrl: cancelUrl,
        client_reference_id: data?.studentId,
      })
      .then((response) => {
        const { data } = response;
        setPaymentSessionUrl(data.data.sessionUrl);
        const newSessionId = data.data.sessionId;
        const currentDate = new Date().toISOString().split("T")[0]; // Get current date in YYYY-MM-DD format

        // Update form data with payment type
        // setFormData(prevFormData => ({
        //     ...prevFormData,
        //     paymentType: newPaymentType
        // }));

        // axios
        //   .post(`registrationadvances/${id}/course-fee-details`, {
        //     sessionId: newSessionId,
        //     sessionDate: currentDate,
        //     amount: amount,
        //     paymentMode: "online",
        //     remarks: "",
        //   })
          // .then((updateResponse) => {
          //   console.log(
          //     "Registration details updated successfully:",
          //     updateResponse.data
          //   );
            //          setOpen(true);
            //        setIsUpdated(false);
          // })
          // .catch((error) => {
          //   console.error("Error updating registration details:", error);
          //   //        toast.error("Error updating registration details. Please try again.");
          // });

        handleCloseDialog(); // Close the dialog
        // window.open(data.data.sessionUrl, '_blank');
        window.location.href = data.data.sessionUrl; // Redirect to the payment page
      })
      .catch((error) => {
        console.error("Error creating payment session:", error);
        setError("Something went wrong, please contact administrative.");
      });
  };

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );

  if (error) return <Alert severity="error">{error}</Alert>;

  const { name, nric, email, batch, enrolledDate } = data || {};
  const totalWanttoPaid = paymentInfo?.totalWanttoPaid || 0;

  const formatCourseType = (type) => {
    if (!type) return "";

    return type
      .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space between lowercase and uppercase letters
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
  };

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 3 }}>
      {/* Centered logo */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <img
          src={logoImage}
          alt="Logo"
          style={{ width: "70%", maxWidth: "280px", height: "auto" }}
        />
      </Box>

      <Typography
        variant="h5"
        component="h1"
        gutterBottom
        align="center"
        sx={{ fontWeight: "bold", fontSize: { xs: "1.25rem", md: "1.5rem" } }}
      >
        Payment Details
      </Typography>

      {data ? (
        <Box>
          <CustomPaper>
            <Typography
              variant="h6"
              sx={{
                mb: 1.5,
                fontWeight: "medium",
                color: "text.secondary",
                fontSize: { xs: "1rem", md: "1.25rem" },
              }}
            >
              Personal Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Name:</strong> {name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>NRIC:</strong> {nric}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Email:</strong> {email}
                </Typography>
              </Grid>
            </Grid>
          </CustomPaper>

          <CustomPaper>
            <Typography
              variant="h6"
              sx={{
                mb: 1.5,
                fontWeight: "medium",
                color: "text.secondary",
                fontSize: { xs: "1rem", md: "1.25rem" },
              }}
            >
              Batch Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">
                  <strong>Type:</strong> {formatType(batch?.type)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">
                  <strong>Course:</strong> {removeBracketedText(batch?.course)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">
                  <strong>Course Type:</strong>{" "}
                  {formatCourseType(batch?.courseType)}
                </Typography>
              </Grid>
              {/* <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'medium', color: 'text.secondary', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                            Enrollment Information
                        </Typography> */}
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">
                  <strong>Enrolled Date:</strong> {enrolledDate}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">
                  <strong>Registration Fee:</strong> {data?.registrationFee}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">
                  <strong>Course Fee :</strong> {data?.courseFee}
                </Typography>
              </Grid>
            </Grid>
          </CustomPaper>

          <CustomPaper>
            <Typography variant="h6" gutterBottom>
              Payment
            </Typography>
            <Typography>Remaining Balance: {totalWanttoPaid} SGD</Typography>
            <TextField
          fullWidth
          label="Amount (SGD)"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            if (errorMessage) {
              setErrorMessage("");
            }
          }}
          type="number"
          placeholder={`Enter amount (Max: ${paymentInfo?.totalWanttoPaid})`}
        />
            {errorMessage && (
              <Typography color="error">{errorMessage}</Typography>
            )}
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleProceed}
                disabled={totalWanttoPaid === 0}
              >
                Proceed to Payment
              </Button>
            </Box>
          </CustomPaper>
        </Box>
      ) : (
        <Typography variant="h6" align="center" color="text.secondary">
          No data available
        </Typography>
      )}

      {/* Payment Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Choose Payment Method</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            You will be redirected to the payment page.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handlePaymentChoice}
            color="primary"
            variant="contained"
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PaymentsPage;
